import { isSubscriptionRequiredForUser } from '@app/utils/isSubscriptionRequiredForUser'

import { analyticsEvent } from '@app/packages/analytics/declaration'
import { resolveThunkSelector } from '@app/packages/thunkSelector/thunkSelector'

import { cardResultSelector } from '@app/store/slices/card'
import { profileUserSelector } from '@app/store/slices/profile'
import { createThunk } from '@app/store/thunk'

import { assertMountPush } from '@app/components/Mount/actions'

import { getAnalyticsRouter } from './analytics'

/**
 * @param _syncHash Used to ensure that profile is updated before calling the action
 */
export function requirePaymentConfigured(_syncHash: string) {
  return createThunk<Promise<boolean>>(async dispatch => {
    const profile = dispatch(getParentWithUnconfiguredPayment())
    if (!profile) return true

    dispatch(getAnalyticsRouter()).send(
      analyticsEvent(() => ({
        // A flow stopped because no payment method was set up.
        type: 'payment_configuration_required',
      })).set('amplitude')
    )

    const card = await dispatch(resolveThunkSelector(cardResultSelector))

    if (profile.payment_methods.current === 'commission' && !card) {
      const cardResultP = Promise.withResolvers<boolean>()
      await dispatch(
        assertMountPush(
          import('@app/components/Payment/PaymentModal').then(m => m.PaymentModal),
          {
            props: {
              mode: 'commission',
              async callback(_card) {
                cardResultP.resolve(!!dispatch(getParentWithUnconfiguredPayment()))
              },
            },
          }
        )
      )
      const cardResult = await cardResultP.promise

      if (!cardResult) return false

      const promocodeP = Promise.withResolvers<void>()
      const closePromocode = await dispatch(
        assertMountPush(
          import('@app/components/PromocodeModal/PromocodeModal').then(m => m.PromocodeModal),
          {
            props: {
              continuable: true,
              onSkip: () => {
                void closePromocode()
              },
            },
            onClose: () => promocodeP.resolve(),
          }
        )
      )
      await promocodeP.promise

      return cardResult
    }

    if (!isSubscriptionRequiredForUser(profile)) return true

    const { promise, resolve } = Promise.withResolvers<boolean>()
    const close = await dispatch(
      assertMountPush(
        import('@app/components/Subscription/SubscriptionModal').then(m => m.SubscriptionModal),
        {
          props: { callback: resolve },
        }
      )
    )
    const result = await promise
    await close()

    return result
  })
}

function getParentWithUnconfiguredPayment() {
  return createThunk((_dispatch, getState) => {
    const data = profileUserSelector(getState())
    return data?.account_type === 'parent' && !data.payment_methods.configured ? data : null
  })
}
